import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Save, Code, AlertTriangle } from 'lucide-react';
import './AdminPanel.css';

const DEFAULT_CSS_TEMPLATE = `/* 
* DESIGN MANAGER - GLOBAL CSS OVERRIDES
* Use this file to customize the look and feel of your website.
* IMPORTANT: Since we use Tailwind CSS, you may need to use !important to override default styles.
*/

/* --- 1. COLOR THEME --- */

/* Primary Dark Color (Text, Dark Backgrounds) */
/* Affects: Headings, Footer, Dark Sections */
:root {
  --color-brand-primary: #20222D; 
}

/* Accent Color (Buttons, Links, Highlights) */
/* Affects: Primary Buttons, Active Links, Icons */
:root {
  --color-brand-accent: #A2CB52; 
}

/* Secondary Accent (Hover States, Borders) */
:root {
  --color-brand-light: #B5D575; 
}

/* Background Color (Light Sections, Cards) */
:root {
  --color-brand-lightest: #ECF5DC; 
}

/* Example: Change the entire site background */
/* body { background-color: #f9f9f9 !important; } */


/* --- 2. TYPOGRAPHY --- */

/* Global Font Settings */
body, h1, h2, h3, h4, h5, h6, p, a, button {
  font-family: 'Inter', sans-serif; 
}

/* Headings H1 */
h1 {
  /* color: #FF0000 !important; */ /* Example: Make H1 Red */
}

/* Body Text */
p {
  /* font-size: 18px !important; */ /* Example: Increase text size */
  /* line-height: 1.8 !important; */
}


/* --- 3. BUTTONS --- */

/* Primary Button (Green) */
.btn-primary {
  /* background-color: #0000FF !important; */ /* Example: Change to Blue */
  /* border-radius: 4px !important; */ /* Example: Square buttons */
}

/* --- 4. LAYOUT --- */

/* Navbar / Header */
header {
  /* background-color: white !important; */
  /* box-shadow: none !important; */
}

/* Footer */
footer {
  /* background-color: #000000 !important; */
}
`;

const DesignManager = () => {
    const [cssCode, setCssCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const fetchDesign = async () => {
            try {
                const docRef = doc(db, 'settings', 'design');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setCssCode(docSnap.data().customCss || '');
                }
            } catch (error) {
                console.error("Error fetching design settings:", error);
            }
        };
        fetchDesign();
    }, []);

    const handleSave = async () => {
        setLoading(true);
        try {
            await setDoc(doc(db, 'settings', 'design'), {
                customCss: cssCode
            }, { merge: true });
            setMessage('Design settings saved successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error saving design settings:", error);
            setMessage('Error saving settings.');
        }
        setLoading(false);
    };

    const loadTemplate = () => {
        if (window.confirm("This will overwrite your current CSS code. Are you sure?")) {
            setCssCode(DEFAULT_CSS_TEMPLATE);
        }
    };

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Look & Feel</div>
                    <h1>Design Manager <span>.</span></h1>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold flex items-center gap-2">
                            <Code size={20} className="text-blue-500" />
                            Global Custom CSS
                        </h2>
                        <div className="flex gap-2">
                            <button
                                onClick={loadTemplate}
                                className="text-sm text-blue-600 hover:text-blue-800 underline"
                            >
                                Load Template / Reset
                            </button>
                        </div>
                    </div>

                    {message && (
                        <div className={`mb-4 text-sm px-3 py-1 rounded-full w-fit ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {message}
                        </div>
                    )}

                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertTriangle className="h-5 w-5 text-blue-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                    Code entered here will be injected into the <code>&lt;head&gt;</code> of every page. Use this to override theme styles.
                                    <br /><strong>Tip:</strong> Use the "Load Template" button to see all available variables and common selectors.
                                </p>
                            </div>
                        </div>
                    </div>

                    <textarea
                        value={cssCode}
                        onChange={(e) => setCssCode(e.target.value)}
                        className="w-full h-96 font-mono text-sm bg-gray-900 text-green-400 p-4 rounded-md border border-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        placeholder="/* Enter your custom CSS here or click 'Load Template' */"
                        spellCheck="false"
                    />

                    <div className="mt-4 flex justify-end">
                        <button
                            onClick={handleSave}
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#A2CB52] hover:bg-[#91b849] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A2CB52] disabled:opacity-50"
                        >
                            <Save size={16} className="mr-2" />
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DesignManager;
